import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, Mail, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const API = `/api`;

const TeacherLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [schoolCode, setSchoolCode] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/teacher/login`, {
        email,
        school_code: schoolCode
      }, { withCredentials: true });
      toast.success(`Welcome! You're connected to ${response.data.organization_name}`);
      onLogin(response.data);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid school code or login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--fe-bg)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#2C4C3B] rounded-2xl mb-4">
            <GraduationCap className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-4xl font-semibold text-[#1C1C1C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>FrameEd</h1>
          <p className="text-[#595959]">Referrals</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8">
          <h2 className="text-xl font-semibold text-[#1C1C1C] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Teacher Login</h2>
          <p className="text-sm text-[#595959] mb-6">Enter your school code and email to get started</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">
                <KeyRound className="w-4 h-4 inline mr-2 text-[#595959]" />
                School Code
              </label>
              <input
                type="text"
                required
                data-testid="school-code-input"
                value={schoolCode}
                onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
                placeholder="e.g. ABC123"
                maxLength={6}
                className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all uppercase tracking-widest text-center font-mono"
              />
              <p className="text-xs text-[#595959] mt-1.5">Your SLP will provide this code</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">
                <Mail className="w-4 h-4 inline mr-2 text-[#595959]" />
                Your School Email
              </label>
              <input
                type="email"
                required
                data-testid="teacher-email-login-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.name@school.edu"
                className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="teacher-login-submit"
              className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium rounded-xl px-6 py-3 transition-colors disabled:bg-[#E5E5E5] disabled:text-[#595959] disabled:cursor-default"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#E5E5E5] text-center">
            <p className="text-sm text-[#595959]">
              Are you an SLP?{' '}
              <Link to="/slp/login" className="text-[#2C4C3B] font-medium hover:underline">
                SLP Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherLogin;
